import React, { useState, useEffect, useRef } from 'react';

import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import { Icon } from 'src/common/components/Icon';
import { Select } from 'src/common/components/Select';
import { SimpleLoader } from 'src/common/components/SimpleLoader';
import { OrbitIcons } from 'src/common/constants.';

import { fetchAccounts, OutboundCarrier } from '../../api/accounts';
import {
  searchCosmosAccounts,
  fetchCosmosAccountWithUsers,
  fetchCosmosIvrConfig,
  fetchCosmosOfficeHours,
  fetchCosmosPhoneSettings,
  CosmosAccountInfo,
  CosmosUser,
} from '../../api/cosmos';
import { FreeSwitchGroup } from '../../api/freeswitchGroups';
import { fetchRingStrategies, RingStrategy } from '../../api/ringStrategies';
import { toE164 } from '../../utils';

import {
  WizardState,
  MockUser,
  MockAudio,
  MockModule,
  MockIvrOption,
  IvrActionType,
  MAX_IVR_DEPTH,
  IvrValidationErrors,
  addIvrChildToTree,
  duplicateIvrOptionInTree,
  nextFreeIvrDigit,
  removeIvrOptionFromTree,
  updateIvrOptionInTree,
  validateIvrTree,
  MockResolutionStrategy,
  MockResolutionTag,
  MockUnavailableReason,
  UNAVAILABLE_REASON_MAX_LENGTH,
  getUnavailableReasonErrors,
  PlaybackType,
  FreeSwitchType,
  SelectedFreeSwitchGroup,
  SelectedCosmosAccount,
  ScheduleTimeSlot,
  DayOfWeek,
  DAY_LABELS,
  createDefaultSchedule,
  flattenIvrOptions,
  getAvailableQueues,
  isIvrFeatureActive,
  moduleQueueKey,
  mapCosmosOfficeHoursToSchedule,
} from './types';
import { clientNameRules, cosmosAccountUuidRules, suggestClientName } from './validation';

const inputClass =
  'w-full rounded-md border px-3 py-2 text-sm transition-colors border-white-2.0 bg-white-0.0 text-black-1.0 placeholder-gray-1.0 focus:border-primary-2.0 focus:outline-none focus:ring-1 focus:ring-primary-2.0 disabled:opacity-60';

const labelClass = 'mb-1 block text-xs font-medium text-gray-3.0';
const errorClass = 'mt-1 block text-xs text-red-600';
const inputErrorClass = 'border-red-500 focus:border-red-500 focus:ring-red-500';
const AWS_CF_CONSOLE_URL =
  'https://us-east-1.console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/create';

const ROLE_OPTIONS = [
  { value: 'agent', label: 'Agente' },
  { value: 'admin', label: 'Admin' },
];

const LANGUAGE_OPTIONS = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'English' },
];

const SectionHeader: React.FC<{
  icon: string;
  iconClass?: string;
  iconBg?: string;
  title: string;
  subtitle: string;
}> = ({ icon, iconClass = 'text-primary-2.0', iconBg = 'bg-primary-2.0/10', title, subtitle }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className={classNames('flex h-10 w-10 items-center justify-center rounded-full', iconBg)}>
      <Icon name={icon} size={20} className={iconClass} />
    </div>
    <div>
      <h3 className="text-lg font-bold text-black-1.0">{title}</h3>
      <p className="text-xs text-gray-2.0">{subtitle}</p>
    </div>
  </div>
);

/** Inline audio preview button - plays the audio when clicked */
const AudioPreviewButton: React.FC<{ audioId: string; audios: MockAudio[] }> = ({
  audioId,
  audios,
}) => {
  const audio = audios.find((a) => a.id === audioId);
  const url = audio?.preview_url || audio?.default_url;
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Stop playback if the button unmounts or the selected audio changes
  useEffect(
    () => () => {
      audioRef.current?.pause();
      audioRef.current = null;
    },
    [url]
  );

  if (!url) return null;

  const toggle = (): void => {
    if (!audioRef.current) {
      audioRef.current = new Audio(url);
      audioRef.current.onended = () => setIsPlaying(false);
    }
    if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
      audioRef.current.play().catch(() => setIsPlaying(false));
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      className="btn btn-ghost shrink-0 px-2"
      title={isPlaying ? 'Pausar' : 'Escuchar'}
    >
      <Icon name={isPlaying ? OrbitIcons.Pause : OrbitIcons.Play} size={14} />
    </button>
  );
};

/* ───────────────────────── Cosmos Search ───────────────────── */

export const CosmosSearchStep: React.FC<{
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
}> = ({ state, onChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CosmosAccountInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [provisionedIds, setProvisionedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const loadProvisionedAccounts = async (): Promise<void> => {
      try {
        const interstellarAccounts = await fetchAccounts();
        const ids = new Set(
          interstellarAccounts
            .map((a) => a.cosmos_account_id?.toLowerCase())
            .filter((id): id is string => !!id)
        );
        setProvisionedIds(ids);
      } catch {
        // Ignore errors fetching Interstellar accounts
      }
    };
    void loadProvisionedAccounts();
  }, []);

  const handleSearch = async (): Promise<void> => {
    if (!query.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const accounts = await searchCosmosAccounts(query);
      const filtered = accounts.filter((a) => !provisionedIds.has(a.id.toLowerCase()));
      setResults(filtered);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (account: CosmosAccountInfo): Promise<void> => {
    const selected: SelectedCosmosAccount = {
      id: account.id,
      name: account.name,
      email: account.email,
      office_phone: account.office_phone,
      timezone: account.timezone,
    };
    onChange({
      cosmos_account: selected,
      cosmos_account_id: account.id,
      account_name: account.name,
      phone_number: account.office_phone
        ? toE164(account.office_phone) ?? account.office_phone.replace(/\D/g, '')
        : '',
      cosmos_ivr: null,
      cosmos_ivr_loading: true,
      cosmos_ivr_unavailable: false,
      use_cosmos_ivr: false,
      cosmos_phone_features: null,
      phone_features: { conference: false, call_whisper: false, unavailable_reasons: false },
    });

    // Fetch IVR config, office hours and phone features from Cosmos in parallel
    const [ivrResult, officeHoursResult, phoneSettingsResult] = await Promise.allSettled([
      fetchCosmosIvrConfig(account.id),
      fetchCosmosOfficeHours(account.id),
      fetchCosmosPhoneSettings(account.id),
    ]);

    // Apply IVR config - if Cosmos has IVR enabled, pre-select "Import existing"
    if (ivrResult.status === 'fulfilled') {
      const ivrConfig = ivrResult.value;
      onChange({
        cosmos_ivr: ivrConfig.enabled ? ivrConfig : null,
        cosmos_ivr_loading: false,
        use_cosmos_ivr: ivrConfig.enabled,
        // Also set ivr.enabled so the radio "Importar existente" is selected by default
        ivr: { ...state.ivr, enabled: ivrConfig.enabled },
      });
    } else {
      onChange({
        cosmos_ivr: null,
        cosmos_ivr_loading: false,
        cosmos_ivr_unavailable: true,
        use_cosmos_ivr: false,
      });
    }

    // Apply office hours if available
    if (officeHoursResult.status === 'fulfilled' && officeHoursResult.value) {
      const schedule = mapCosmosOfficeHoursToSchedule(officeHoursResult.value);
      onChange({ schedule });
    }

    // Start the phone features from what the account already has, so the
    // wizard doesn't turn off a feature nobody asked to remove
    if (phoneSettingsResult.status === 'fulfilled') {
      const features = phoneSettingsResult.value.enabled_phone_features;
      onChange({
        cosmos_phone_features: features,
        phone_features: {
          conference: features.includes('conference'),
          call_whisper: features.includes('call_whisper'),
          unavailable_reasons: features.includes('unavailable_reasons'),
        },
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void handleSearch();
    }
  };

  return (
    <div>
      <SectionHeader
        icon={OrbitIcons.Search}
        iconClass="text-primary-2.0"
        iconBg="bg-primary-2.0/10"
        title="Selecciona la cuenta"
        subtitle="Busca la oficina o consultorio que recibirá el softphone"
      />

      <div className="space-y-4">
        {/* Search input */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <div className="left-3 absolute inset-y-0 flex items-center pointer-events-none">
              <Icon name={OrbitIcons.Search} size={16} className="text-gray-400" />
            </div>
            <input
              type="text"
              className={classNames(inputClass, 'pl-9')}
              placeholder="Nombre de la oficina..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
          </div>
          <button
            type="button"
            onClick={() => void handleSearch()}
            disabled={loading || !query.trim()}
            className="btn btn-primary disabled:opacity-50 shrink-0 flex items-center gap-2"
          >
            {loading && <Icon name={OrbitIcons.RefreshCw} size={16} className="animate-spin" />}
            Buscar
          </button>
        </div>

        {/* Selected account */}
        {state.cosmos_account && (
          <div className="bg-green-50 flex items-center gap-3 p-4 border border-green-200 rounded-lg">
            <div className="shrink-0 flex items-center justify-center w-10 h-10 bg-green-100 rounded-full">
              <Icon name={OrbitIcons.CheckCircle2} size={20} className="text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-green-900 truncate">
                {state.cosmos_account.name}
              </p>
              <p className="text-xs text-green-700 truncate">
                {[state.cosmos_account.email, state.cosmos_account.office_phone]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                onChange({
                  cosmos_account: null,
                  cosmos_account_id: '',
                  account_name: '',
                  phone_number: '',
                  cosmos_ivr: null,
                  cosmos_ivr_loading: false,
                  use_cosmos_ivr: false,
                  // Reset data loaded from the previous account
                  cosmos_phone_features: null,
                  phone_features: {
                    conference: false,
                    call_whisper: false,
                    unavailable_reasons: false,
                  },
                  users: [],
                  cosmos_users_loaded: false,
                  schedule: createDefaultSchedule(),
                  ivr: { ...state.ivr, enabled: false, options: [] },
                })
              }
              className="shrink-0 text-xs font-medium text-gray-2.0 hover:text-red-600"
            >
              Cambiar
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center gap-2 py-12 text-gray-2.0">
            <SimpleLoader />
            <span className="text-sm">Buscando...</span>
          </div>
        )}

        {/* Search results */}
        {!loading && !state.cosmos_account && searched && (
          <div className="space-y-2">
            {results.length === 0 ? (
              <div className="border-amber-200 bg-amber-50 p-4 text-center border rounded-lg">
                <Icon
                  name={OrbitIcons.AlertCircle}
                  size={24}
                  className="text-amber-500 mx-auto mb-2"
                />
                <p className="text-amber-800 text-sm font-medium">No encontramos esa cuenta</p>
                <p className="text-amber-700 text-xs">Verifica el nombre e intenta de nuevo</p>
              </div>
            ) : (
              <div className="divide-y divide-white-2.0 overflow-hidden rounded-lg border border-white-2.0">
                <p className="bg-white-1.0 px-3 py-2 text-xs font-medium text-gray-2.0">
                  {results.length} resultado{results.length !== 1 && 's'}
                </p>
                <div className="max-h-64 overflow-y-auto">
                  {results.map((account) => (
                    <button
                      key={account.id}
                      type="button"
                      onClick={() => handleSelect(account)}
                      className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-primary-2.0/5"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white-1.0">
                        <Icon name={OrbitIcons.Building} size={16} className="text-gray-2.0" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-medium text-black-1.0">
                          {account.name}
                        </p>
                        {account.email && (
                          <p className="truncate text-xs text-gray-2.0">{account.email}</p>
                        )}
                      </div>
                      <Icon
                        name={OrbitIcons.ChevronRight}
                        size={16}
                        className="shrink-0 text-gray-2.0"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty state */}
        {!state.cosmos_account && !searched && !loading && (
          <div className="py-8 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white-1.0">
              <Icon name={OrbitIcons.Search} size={24} className="text-gray-2.0" />
            </div>
            <p className="text-sm font-medium text-black-1.0">Busca tu cuenta</p>
            <p className="text-xs text-gray-2.0">Escribe el nombre de la oficina arriba</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ───────────────────────── FreeSWITCH Type ──────────────────── */

export const FreeSwitchTypeStep: React.FC<{
  state: WizardState;
  groups: FreeSwitchGroup[];
  loading: boolean;
  onChange: (patch: Partial<WizardState>) => void;
}> = ({ state, groups, loading, onChange }) => {
  const [manualMode, setManualMode] = useState(false);

  const handleTypeChange = (type: FreeSwitchType): void => {
    // Reset manual mode when changing type
    setManualMode(false);
    onChange({
      freeswitch_type: type,
      selected_group: type === 'dedicated' ? null : state.selected_group,
      // Reset infrastructure state when switching types
      infra_ready: false,
      instance_id: '',
      efs_access_point_id: '',
      stack_name: '',
      stack_status: '',
      stack_console_url: '',
      pg_database: '',
      provisioned_client_name: '',
      freeswitch_uri:
        type === 'shared' && state.selected_group ? state.selected_group.freeswitch_uri : '',
    });
  };

  const handleGroupSelect = (groupId: number): void => {
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;

    const selectedGroup: SelectedFreeSwitchGroup = {
      id: group.id,
      name: group.name,
      freeswitch_uri: group.freeswitch_uri,
      freeswitch_id: group.freeswitch_id,
    };

    onChange({
      selected_group: selectedGroup,
      freeswitch_uri: group.freeswitch_uri,
      infra_ready: true, // Shared groups are already ready
    });
  };

  // Auto-select logic: if there's exactly one group with capacity, select it automatically
  const eligibleGroups = groups.filter((g) => g.account_count < g.max_accounts);
  const shouldAutoSelect =
    state.freeswitch_type === 'shared' && !manualMode && !loading && eligibleGroups.length === 1;
  const autoSelectedGroup = shouldAutoSelect ? eligibleGroups[0] : null;

  // Auto-select when conditions are met
  useEffect(() => {
    if (autoSelectedGroup && state.selected_group?.id !== autoSelectedGroup.id) {
      handleGroupSelect(autoSelectedGroup.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectedGroup?.id]);

  // Determine the current state for UI rendering
  const allGroupsFull = groups.length > 0 && eligibleGroups.length === 0;
  const isAutoMode = shouldAutoSelect && state.selected_group;
  const showManualSelect = manualMode || (!shouldAutoSelect && eligibleGroups.length > 0);

  return (
    <div>
      <SectionHeader
        icon={OrbitIcons.Cloud}
        iconClass="text-primary-2.0"
        iconBg="bg-primary-2.0/10"
        title="Plan de servicio"
        subtitle="Selecciona según el tamaño de la oficina"
      />

      <div className="space-y-4">
        {/* Radio buttons for type selection */}
        <div className="space-y-3">
          <label
            className={classNames(
              'flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors',
              state.freeswitch_type === 'dedicated'
                ? 'border-primary-2.0 bg-primary-2.0/5'
                : 'border-white-2.0 hover:border-gray-1.0'
            )}
          >
            <input
              type="radio"
              name="freeswitch_type"
              value="dedicated"
              checked={state.freeswitch_type === 'dedicated'}
              onChange={() => handleTypeChange('dedicated')}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-medium text-black-1.0">Enterprise</div>
              <div className="text-sm text-gray-2.0">
                Para oficinas grandes con alto volumen de llamadas. Servidor dedicado con recursos
                garantizados.
              </div>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-2.0">
                <span className="px-2 py-1 bg-gray-100 rounded">+500 llamadas/día</span>
                <span className="px-2 py-1 bg-gray-100 rounded">+50 agentes</span>
              </div>
            </div>
          </label>

          <label
            className={classNames(
              'flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors',
              state.freeswitch_type === 'shared'
                ? 'border-primary-2.0 bg-primary-2.0/5'
                : 'border-white-2.0 hover:border-gray-1.0'
            )}
          >
            <input
              type="radio"
              name="freeswitch_type"
              value="shared"
              checked={state.freeswitch_type === 'shared'}
              onChange={() => handleTypeChange('shared')}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-medium text-black-1.0">SMB / Pequeños Negocios</div>
              <div className="text-sm text-gray-2.0">
                Para oficinas pequeñas y medianas. Recursos compartidos, menor costo.
              </div>
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-2.0">
                <span className="px-2 py-1 bg-gray-100 rounded">&lt;500 llamadas/día</span>
                <span className="px-2 py-1 bg-gray-100 rounded">&lt;50 agentes</span>
              </div>
            </div>
          </label>
        </div>

        {/* Group selector (only shown when shared is selected) */}
        {state.freeswitch_type === 'shared' && (
          <div className="mt-4">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-2.0 py-2">
                <Icon name={OrbitIcons.RefreshCw} size={16} className="animate-spin" />
                Cargando grupos disponibles...
              </div>
            ) : groups.length === 0 ? (
              /* No groups available at all */
              <div className="border-amber-200 bg-amber-50 p-4 border rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name={OrbitIcons.AlertCircle} size={20} className="mt-0.5 text-amber-600" />
                  <div>
                    <p className="text-amber-800 font-medium">
                      No hay grupos de FreeSWITCH disponibles
                    </p>
                    <p className="text-amber-700 mt-1 text-sm">
                      Debes crear un grupo compartido antes de continuar, o selecciona
                      &quot;Enterprise&quot; para un servidor dedicado.
                    </p>
                    <a
                      href="/app/phone_settings/fs_groups"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-amber-600 hover:bg-amber-700 inline-flex items-center gap-2 px-4 py-2 mt-3 text-sm font-medium text-white transition-colors rounded-lg"
                    >
                      <Icon name={OrbitIcons.PlusCircle} size={16} />
                      Crear grupo FreeSWITCH
                    </a>
                  </div>
                </div>
              </div>
            ) : allGroupsFull ? (
              /* All groups are at capacity */
              <div className="bg-red-50 p-4 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name={OrbitIcons.AlertCircle} size={20} className="mt-0.5 text-red-600" />
                  <div>
                    <p className="font-medium text-red-800">Todos los grupos están llenos</p>
                    <p className="mt-1 text-sm text-red-700">
                      Todos los grupos están al máximo de cuentas. Crea un nuevo grupo para
                      continuar.
                    </p>
                    <a
                      href="/app/phone_settings/fs_groups"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:bg-red-700 inline-flex items-center gap-2 px-4 py-2 mt-3 text-sm font-medium text-white transition-colors bg-red-600 rounded-lg"
                    >
                      <Icon name={OrbitIcons.PlusCircle} size={16} />
                      Crear nuevo grupo
                    </a>
                  </div>
                </div>
              </div>
            ) : isAutoMode ? (
              /* Auto-selected mode: one eligible group */
              <div className="bg-green-50 p-4 border border-green-200 rounded-lg">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <Icon
                      name={OrbitIcons.CheckCircle2}
                      size={20}
                      className="mt-0.5 text-green-600"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-green-800">Grupo auto-seleccionado</p>
                        <span className="rounded-full bg-green-200 px-2 py-0.5 text-xs font-medium text-green-800">
                          Automático
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-green-700">
                        <span className="font-semibold">{state.selected_group?.name}</span>
                        {autoSelectedGroup && (
                          <span className="ml-2 text-green-600">
                            ({autoSelectedGroup.account_count}/{autoSelectedGroup.max_accounts}{' '}
                            cuentas)
                          </span>
                        )}
                      </p>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-green-700">
                        <div>
                          <span className="text-green-600">URI:</span>{' '}
                          <span className="font-mono">{state.selected_group?.freeswitch_uri}</span>
                        </div>
                        <div>
                          <span className="text-green-600">ID:</span>{' '}
                          <span className="font-mono">{state.selected_group?.freeswitch_id}</span>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-green-600">
                        Este es el único grupo disponible con capacidad.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setManualMode(true)}
                    className="shrink-0 rounded-lg border border-green-300 bg-white px-3 py-1.5 text-xs font-medium text-green-700 transition-colors hover:bg-green-100"
                  >
                    Cambiar manual
                  </button>
                </div>
              </div>
            ) : showManualSelect ? (
              /* Manual selection mode */
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="wiz-group" className={labelClass}>
                    Seleccionar grupo *
                  </label>
                  {manualMode && (
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                      Modo manual
                    </span>
                  )}
                </div>
                <Select
                  inputId="wiz-group"
                  options={eligibleGroups.map((g) => ({
                    ...g,
                    label: `${g.name} (${g.account_count}/${g.max_accounts} cuentas)`,
                  }))}
                  fieldId="id"
                  fieldLabel="label"
                  value={state.selected_group?.id || null}
                  onChange={(opt: unknown) => {
                    const selected = opt as { id: number } | null;
                    if (selected) handleGroupSelect(selected.id);
                  }}
                  placeholder="-- Seleccionar grupo --"
                  isClearable={false}
                  menuPortalTarget={document.body}
                />

                {state.selected_group && (
                  <div className="mt-3 rounded-lg border border-white-2.0 bg-white-1.0 p-3 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-gray-2.0">URI:</span>{' '}
                        <span className="font-mono text-black-1.0">
                          {state.selected_group.freeswitch_uri}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-2.0">ID:</span>{' '}
                        <span className="font-mono text-xs text-black-1.0">
                          {state.selected_group.freeswitch_id}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {eligibleGroups.length > 1 && !manualMode && (
                  <p className="mt-2 text-xs text-gray-2.0">
                    Hay {eligibleGroups.length} grupos disponibles. Selecciona uno.
                  </p>
                )}
              </div>
            ) : null}
          </div>
        )}

        {/* Max users field */}
        <div className="mt-6 pt-4 border-t border-white-2.0">
          <label htmlFor="wiz-max-users" className={labelClass}>
            Máximo de usuarios (opcional)
          </label>
          <input
            id="wiz-max-users"
            type="number"
            min={1}
            placeholder="Sin límite"
            value={state.max_users ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              onChange({ max_users: val ? parseInt(val, 10) : null });
            }}
            className={classNames(inputClass, 'w-32')}
          />
          <p className="mt-1 text-xs text-gray-2.0">
            Límite de usuarios que se pueden crear en el siguiente paso. Dejar vacío para no tener
            límite.
          </p>
        </div>
      </div>
    </div>
  );
};

/* ───────────────────────── Infrastructure ──────────────────── */

interface InfrastructureFormValues {
  cosmos_account_id: string;
  client_name: string;
}

export const InfrastructureStep: React.FC<{
  state: WizardState;
  provisioning: boolean;
  onChange: (patch: Partial<WizardState>) => void;
  onProvision: () => void;
  prototype: boolean;
}> = ({ state, provisioning, onChange, onProvision, prototype }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InfrastructureFormValues>({
    mode: 'onBlur',
    defaultValues: { cosmos_account_id: state.cosmos_account_id, client_name: state.client_name },
  });

  const cosmosField = register('cosmos_account_id', cosmosAccountUuidRules);
  const clientField = register('client_name', clientNameRules);
  const clientNameHint = errors.client_name ? suggestClientName(state.client_name) : '';

  const updateInfrastructure = (patch: Partial<WizardState>): void => {
    onChange({
      ...patch,
      infra_ready: false,
      instance_id: '',
      efs_access_point_id: '',
      stack_name: '',
      stack_status: '',
      stack_console_url: '',
      pg_database: '',
      provisioned_client_name: '',
    });
  };

  return (
    <form onSubmit={handleSubmit(onProvision)} noValidate>
      <SectionHeader
        icon={OrbitIcons.Cloud}
        iconClass="text-amber-600"
        iconBg="bg-amber-100"
        title="Servidor dedicado"
        subtitle={
          prototype
            ? 'Genera datos de prueba para continuar el prototipo'
            : 'Configura el servidor exclusivo para esta cuenta (solo equipo técnico)'
        }
      />

      <div className="space-y-4">
        <div className="gap-x-4 gap-y-3 grid grid-cols-2">
          <div>
            <label htmlFor="wiz-cosmos" className={labelClass}>
              Cosmos Account UUID *
            </label>
            <input
              {...cosmosField}
              id="wiz-cosmos"
              className={classNames(inputClass, { [inputErrorClass]: !!errors.cosmos_account_id })}
              value={state.cosmos_account_id}
              disabled={provisioning}
              aria-invalid={!!errors.cosmos_account_id}
              onChange={(e) => {
                void cosmosField.onChange(e);
                updateInfrastructure({
                  cosmos_account_id: e.target.value,
                  cosmos_phone_features: null,
                });
              }}
            />
            {errors.cosmos_account_id && (
              <span className={errorClass}>{String(errors.cosmos_account_id.message)}</span>
            )}
          </div>
          <div>
            <label htmlFor="wiz-client" className={labelClass}>
              Client Name *
            </label>
            <input
              {...clientField}
              id="wiz-client"
              className={classNames(inputClass, { [inputErrorClass]: !!errors.client_name })}
              value={state.client_name}
              disabled={provisioning}
              aria-invalid={!!errors.client_name}
              onChange={(e) => {
                void clientField.onChange(e);
                updateInfrastructure({ client_name: e.target.value });
              }}
            />
            {errors.client_name && (
              <span className={errorClass}>
                {String(errors.client_name.message)}
                {clientNameHint && ` Prueba con "${clientNameHint}".`}
              </span>
            )}
          </div>
        </div>

        {!prototype && (
          <div className="flex flex-wrap gap-3">
            <a
              href={state.stack_console_url || AWS_CF_CONSOLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-amber-400 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-700 transition-colors hover:bg-amber-100"
            >
              <Icon name={OrbitIcons.ExternalLink} size={16} />
              Abrir CloudFormation
            </a>
          </div>
        )}

        {provisioning && (
          <div className="bg-amber-50 border-amber-200 flex items-start gap-3 p-4 border rounded-lg">
            <span className="animate-spin border-t-transparent mt-0.5 w-5 h-5 border-2 border-amber-600 rounded-full shrink-0" />
            <div>
              <p className="text-amber-900 text-sm font-semibold">
                {prototype ? 'Generando datos de prueba...' : 'Desplegando FreeSWITCH...'}
              </p>
              <p className="text-amber-800 mt-1 text-xs">
                {prototype
                  ? 'Esta accion solo esta disponible en Mirage.'
                  : `${state.stack_name || 'Creando stack'}${
                      state.stack_status ? ` — ${state.stack_status}` : ''
                    }. El despliegue tarda varios minutos; si cierras el modal continua en AWS, pero se detiene el seguimiento.`}
              </p>
            </div>
          </div>
        )}

        {state.infra_ready && !provisioning && (
          <div className="bg-green-50 p-4 space-y-1 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-green-900">Stack listo</p>
            </div>
            <ul className="font-mono text-xs text-green-800 space-y-0.5">
              {state.stack_name && <li>Stack: {state.stack_name}</li>}
              <li>InstanceId: {state.instance_id}</li>
              <li>EFSAccessPoint: {state.efs_access_point_id}</li>
              {state.pg_database && <li>PostgreSQL: {state.pg_database}</li>}
            </ul>
          </div>
        )}

        <button
          type="submit"
          disabled={provisioning}
          className="btn btn-secondary disabled:opacity-50 inline-flex items-center gap-2"
        >
          <Icon name={OrbitIcons.Cloud} size={16} />
          {prototype
            ? state.infra_ready
              ? 'Regenerar datos de prueba'
              : 'Generar datos de prueba'
            : state.stack_name
            ? 'Actualizar estado del stack'
            : 'Aprovisionar FreeSWITCH'}
        </button>
      </div>
    </form>
  );
};

/* ───────────────────────── Account ─────────────────────────── */

const OUTBOUND_CARRIER_OPTIONS: { value: OutboundCarrier; label: string }[] = [
  { value: 'twilio', label: 'Twilio' },
  { value: 'telnyx', label: 'Telnyx' },
];

/** Phone fields Interstellar dials as-is, so they must carry the country code. */
const PHONE_FIELD_MESSAGES: Partial<Record<keyof WizardState, string>> = {
  phone_number:
    'Debe ser un número de 10 dígitos; se le agrega el +1 porque es el caller ID de salida.',
  call_rescue_number:
    'Debe ser un número de 10 dígitos; se le agrega el +1 porque es el número de call rescue.',
};

const OutboundCarrierField: React.FC<{
  value: OutboundCarrier;
  onChange: (value: OutboundCarrier) => void;
}> = ({ value, onChange }) => (
  <div>
    <label htmlFor="wiz-acc-outbound-carrier" className={labelClass}>
      Proveedor del teléfono principal
    </label>
    <Select
      inputId="wiz-acc-outbound-carrier"
      options={OUTBOUND_CARRIER_OPTIONS}
      value={value}
      onChange={(opt: unknown) => {
        const selected = opt as { value: OutboundCarrier } | null;
        if (selected) onChange(selected.value);
      }}
    />
    <span className="mt-1 block text-xs text-gray-2.0">
      El número debe estar comprado o portado en este proveedor; las llamadas salientes salen por
      él.
    </span>
  </div>
);

export const AccountStep: React.FC<{
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
}> = ({ state, onChange }) => {
  const isShared = state.freeswitch_type === 'shared';

  // Base fields shown for both modes
  const baseFields: [keyof WizardState, string, string][] = [
    ['account_name', 'Nombre de la cuenta', 'text'],
    ['phone_number', 'Teléfono principal', 'text'],
    ['call_rescue_number', 'Teléfono de respaldo', 'text'],
    ['admin_username', 'Usuario administrador', 'text'],
    ['admin_password', 'Contraseña', 'password'],
  ];

  // Additional fields only for dedicated mode
  const dedicatedFields: [keyof WizardState, string, string][] = [
    ['freeswitch_uri', 'URI del servidor', 'text'],
    ['instance_id', 'ID del servidor', 'text'],
  ];

  const fields = isShared ? baseFields : [...baseFields, ...dedicatedFields];

  return (
    <>
      <SectionHeader
        icon={OrbitIcons.Building}
        title="Datos de la cuenta"
        subtitle="Información básica del softphone"
      />

      {isShared && state.selected_group && (
        <div className="bg-blue-50 p-3 mb-4 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            <span className="font-semibold">Servidor asignado:</span> {state.selected_group.name}
          </p>
        </div>
      )}

      <div className="gap-x-4 gap-y-3 grid grid-cols-2">
        {fields.map(([key, label, type]) => {
          const value = state[key] as string;
          const phoneMessage = PHONE_FIELD_MESSAGES[key];
          const phoneInvalid = !!phoneMessage && value.trim() !== '' && toE164(value) === null;
          return (
            <React.Fragment key={key}>
              <div className={key === 'freeswitch_uri' ? 'col-span-2' : ''}>
                <label htmlFor={`wiz-acc-${key}`} className={labelClass}>
                  {label}
                </label>
                <input
                  id={`wiz-acc-${key}`}
                  type={type}
                  className={classNames(inputClass, phoneInvalid && inputErrorClass)}
                  placeholder={phoneMessage ? '+17875551234' : undefined}
                  aria-invalid={phoneInvalid || undefined}
                  value={value}
                  onChange={(e) => onChange({ [key]: e.target.value })}
                  onBlur={
                    phoneMessage
                      ? () => {
                          const e164 = toE164(value);
                          if (e164 && e164 !== value) onChange({ [key]: e164 });
                        }
                      : undefined
                  }
                />
                {phoneInvalid && <span className={errorClass}>{phoneMessage}</span>}
              </div>
              {key === 'phone_number' && (
                <OutboundCarrierField
                  value={state.outbound_carrier}
                  onChange={(outbound_carrier) => onChange({ outbound_carrier })}
                />
              )}
            </React.Fragment>
          );
        })}

        {/* cosmos_account_id: needed for both modes, but may be pre-filled in shared mode */}
        <div>
          <label htmlFor="wiz-acc-cosmos" className={labelClass}>
            Cosmos Account UUID
          </label>
          <input
            id="wiz-acc-cosmos"
            type="text"
            className={inputClass}
            value={state.cosmos_account_id}
            onChange={(e) =>
              onChange({ cosmos_account_id: e.target.value, cosmos_phone_features: null })
            }
          />
        </div>

        {/* PostgreSQL only for dedicated mode */}
        {!isShared && (
          <div className="col-span-2">
            <label htmlFor="wiz-acc-pg" className={labelClass}>
              PostgreSQL
            </label>
            {state.provisioned_client_name ? (
              <p className="text-xs text-gray-2.0">
                Base de datos aprovisionada: <span className="font-mono">{state.pg_database}</span>.
                La contrasena permanece en Secrets Manager y la resuelve Interstellar al crear la
                cuenta.
              </p>
            ) : (
              <input
                id="wiz-acc-pg"
                type="password"
                className={inputClass}
                placeholder="Connection string (solo para un FreeSWITCH creado a mano)"
                value={state.pg_connection_string}
                onChange={(e) => onChange({ pg_connection_string: e.target.value })}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};

/* ───────────────────────── Users ───────────────────────────── */

/** Toggle switch component for call center flag */
const Switch: React.FC<{
  on: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
  label: string;
}> = ({ on, disabled = false, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!on)}
    className={classNames(
      'relative inline-flex flex-shrink-0 items-center h-5 w-9 rounded-full transition-colors',
      'focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-2.0 disabled:opacity-50',
      on ? 'bg-green-500' : 'bg-gray-300'
    )}
  >
    <span
      className={classNames(
        'inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform',
        on ? 'translate-x-4' : 'translate-x-1'
      )}
    />
  </button>
);

/** Maps a Cosmos user to our MockUser format with auto-assigned extension */
const cosmosUserToMockUser = (user: CosmosUser, index: number): MockUser => ({
  id: `cosmos_${user.id}`,
  first_name: user.first_name || user.username,
  last_name: user.last_name || '',
  email: user.email,
  cosmos_user_id: user.id,
  extension: String(1000 + index),
  is_admin: user.is_account_manager,
  in_call_center: true,
  from_cosmos: true,
});

export const UsersStep: React.FC<{
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
}> = ({ state, onChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { users, cosmos_account, cosmos_users_loaded, max_users } = state;

  // Validation: check if user count exceeds the configured limit
  const exceedsLimit = max_users !== null && users.length > max_users;

  // Load users from Cosmos when account is selected and not yet loaded
  useEffect(() => {
    if (!cosmos_account || cosmos_users_loaded) return;

    const loadUsers = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchCosmosAccountWithUsers(cosmos_account.id);
        if (result && result.users.length > 0) {
          const mappedUsers = result.users
            .filter((u) => u.is_active)
            .map((u, i) => cosmosUserToMockUser(u, i));
          onChange({
            users: mappedUsers,
            cosmos_users_loaded: true,
          });
        } else {
          onChange({ cosmos_users_loaded: true });
        }
      } catch {
        setError('Error al cargar usuarios de Cosmos');
        onChange({ cosmos_users_loaded: true });
      } finally {
        setLoading(false);
      }
    };

    void loadUsers();
  }, [cosmos_account, cosmos_users_loaded, onChange]);

  const updateUser = (id: string, patch: Partial<MockUser>): void => {
    onChange({ users: users.map((u) => (u.id === id ? { ...u, ...patch } : u)) });
  };

  const removeUser = (id: string): void => {
    onChange({ users: users.filter((u) => u.id !== id) });
  };

  const cosmosUsers = users.filter((u) => u.from_cosmos);
  const manualUsers = users.filter((u) => !u.from_cosmos);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name={OrbitIcons.RefreshCw} size={24} className="animate-spin text-primary-2.0" />
        <span className="ml-3 text-gray-2.0">Cargando usuarios de Cosmos...</span>
      </div>
    );
  }

  return (
    <>
      <SectionHeader
        icon={OrbitIcons.UserPlus}
        title="Equipo telefónico"
        subtitle="¿Quiénes usarán el softphone?"
      />

      {exceedsLimit && (
        <div className="bg-red-50 flex items-center gap-2 p-3 mb-4 text-sm text-red-800 border border-red-200 rounded-lg">
          <Icon name={OrbitIcons.AlertCircle} size={16} />
          <span>
            Has seleccionado <strong>{users.length}</strong> usuarios pero el límite configurado es{' '}
            <strong>{max_users}</strong>. Elimina {users.length - max_users} usuario(s) para
            continuar.
          </span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 p-3 mb-4 text-sm text-red-800 border border-red-200 rounded-lg">
          {error}
        </div>
      )}

      {/* Cosmos Users Section */}
      {cosmosUsers.length > 0 && (
        <div className="mb-6">
          <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-black-1.0">
            <Icon name="users" size={16} className="text-primary-2.0" />
            Usuarios encontrados ({cosmosUsers.length})
          </h4>
          <div className="space-y-2">
            {cosmosUsers.map((user) => (
              <div
                key={user.id}
                className="rounded-lg border border-white-2.0 bg-white-1.0 p-3 grid grid-cols-12 gap-3 items-center"
              >
                {/* User Info (read-only) - spans 4 cols */}
                <div className="col-span-4">
                  <p className="text-sm font-medium text-black-1.0">
                    {user.first_name} {user.last_name}
                  </p>
                  {user.email && <p className="text-xs text-gray-2.0">{user.email}</p>}
                </div>

                {/* Extension - spans 2 cols */}
                <div className="col-span-2">
                  <label className={labelClass}>Extensión</label>
                  <input
                    className={inputClass}
                    value={user.extension}
                    onChange={(e) => updateUser(user.id, { extension: e.target.value })}
                  />
                </div>

                {/* Role - spans 2 cols */}
                <div className="col-span-2">
                  <label className={labelClass}>Rol</label>
                  <Select
                    options={ROLE_OPTIONS}
                    value={user.is_admin ? 'admin' : 'agent'}
                    onChange={(opt: unknown) => {
                      const selected = opt as { value: string } | null;
                      updateUser(user.id, { is_admin: selected?.value === 'admin' });
                    }}
                    menuPortalTarget={document.body}
                  />
                </div>

                {/* Call Center Switch - spans 3 cols */}
                <div className="flex items-center col-span-3 gap-2">
                  <Switch
                    on={user.in_call_center}
                    onChange={(value) => updateUser(user.id, { in_call_center: value })}
                    label={`${user.first_name} puede recibir llamadas entrantes`}
                  />
                  <span className="text-xs text-gray-2.0">Recibir llamadas</span>
                </div>

                {/* Empty space for alignment - spans 1 col */}
                <div className="col-span-1" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual Users Section */}
      {manualUsers.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-black-1.0 mb-3 flex items-center gap-2">
            <Icon name={OrbitIcons.UserPlus} size={16} className="text-gray-2.0" />
            Usuarios adicionales ({manualUsers.length})
          </h4>
          <div className="space-y-2">
            {manualUsers.map((user) => (
              <div
                key={user.id}
                className="rounded-lg border border-white-2.0 bg-white-1.0 p-3 grid grid-cols-12 gap-3 items-end"
              >
                {/* Name */}
                <div className="col-span-2">
                  <label className={labelClass}>Nombre</label>
                  <input
                    className={inputClass}
                    placeholder="Nombre"
                    value={user.first_name}
                    onChange={(e) => updateUser(user.id, { first_name: e.target.value })}
                  />
                </div>

                {/* Last Name */}
                <div className="col-span-2">
                  <label className={labelClass}>Apellido</label>
                  <input
                    className={inputClass}
                    placeholder="Apellido"
                    value={user.last_name}
                    onChange={(e) => updateUser(user.id, { last_name: e.target.value })}
                  />
                </div>

                {/* Extension */}
                <div className="col-span-2">
                  <label className={labelClass}>Extensión</label>
                  <input
                    className={inputClass}
                    value={user.extension}
                    onChange={(e) => updateUser(user.id, { extension: e.target.value })}
                  />
                </div>

                {/* Role */}
                <div className="col-span-2">
                  <label className={labelClass}>Rol</label>
                  <Select
                    options={ROLE_OPTIONS}
                    value={user.is_admin ? 'admin' : 'agent'}
                    onChange={(opt: unknown) => {
                      const selected = opt as { value: string } | null;
                      updateUser(user.id, { is_admin: selected?.value === 'admin' });
                    }}
                    menuPortalTarget={document.body}
                  />
                </div>

                {/* Call Center Switch */}
                <div className="flex items-center col-span-2 gap-2 pb-1">
                  <Switch
                    on={user.in_call_center}
                    onChange={(value) => updateUser(user.id, { in_call_center: value })}
                    label={`${user.first_name} puede recibir llamadas entrantes`}
                  />
                  <span className="text-xs text-gray-2.0">Recibir llamadas</span>
                </div>

                {/* Remove button */}
                <div className="flex justify-end col-span-2 pb-1">
                  <button
                    type="button"
                    onClick={() => removeUser(user.id)}
                    className="btn btn-ghost text-xs text-red-600"
                  >
                    <Icon name={OrbitIcons.Trash} size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info message when no users */}
      {users.length === 0 && !loading && (
        <div className="bg-amber-50 border-amber-200 p-4 mt-4 border rounded-lg">
          <p className="text-amber-800 text-sm">
            No se encontraron usuarios en Cosmos para esta cuenta. Agrega usuarios en Cosmos primero
            y luego vuelve a este paso.
          </p>
        </div>
      )}
    </>
  );
};

/* ───────────────────────── Schedule ──────────────────────────── */

const DAYS_ORDER: DayOfWeek[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const ScheduleStep: React.FC<{
  schedule: WizardState['schedule'];
  timezone: string | undefined;
  onChange: (schedule: WizardState['schedule']) => void;
}> = ({ schedule, timezone, onChange }) => {
  const updateTimeSlot = (dayOfWeek: DayOfWeek, patch: Partial<ScheduleTimeSlot>): void => {
    onChange({
      time_slots: schedule.time_slots.map((slot) =>
        slot.day_of_week === dayOfWeek ? { ...slot, ...patch } : slot
      ),
    });
  };

  const copyToAllDays = (sourceDay: DayOfWeek): void => {
    const sourceSlot = schedule.time_slots.find((s) => s.day_of_week === sourceDay);
    if (!sourceSlot) return;

    onChange({
      time_slots: schedule.time_slots.map((slot) => ({
        ...slot,
        is_open: sourceSlot.is_open,
        open_time: sourceSlot.open_time,
        close_time: sourceSlot.close_time,
      })),
    });
  };

  const setWeekdaysOpen = (): void => {
    onChange({
      time_slots: schedule.time_slots.map((slot) => ({
        ...slot,
        is_open: ['mon', 'tue', 'wed', 'thu', 'fri'].includes(slot.day_of_week),
      })),
    });
  };

  return (
    <>
      <SectionHeader
        icon={OrbitIcons.Clock}
        title="Horario de atención"
        subtitle="¿Cuándo está abierta la oficina?"
      />

      {timezone && (
        <div className="bg-blue-50 p-3 mb-4 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            <Icon name={OrbitIcons.Globe} size={12} className="inline mr-1" />
            Zona horaria: <span className="font-medium">{timezone}</span>
          </p>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <button type="button" onClick={setWeekdaysOpen} className="btn btn-ghost text-xs">
          Solo dias de semana
        </button>
        <button
          type="button"
          onClick={() => copyToAllDays('mon')}
          className="btn btn-ghost text-xs"
        >
          Copiar Lunes a todos
        </button>
      </div>

      <div className="space-y-2">
        {DAYS_ORDER.map((day) => {
          const slot = schedule.time_slots.find((s) => s.day_of_week === day);
          if (!slot) return null;

          return (
            <div
              key={day}
              className={classNames(
                'flex items-center gap-4 rounded-lg border p-3 transition-colors',
                slot.is_open ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
              )}
            >
              {/* Day toggle */}
              <label className="w-28 flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={slot.is_open}
                  onChange={(e) => updateTimeSlot(day, { is_open: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary-2.0 focus:ring-primary-2.0"
                />
                <span
                  className={classNames(
                    'text-sm font-medium',
                    slot.is_open ? 'text-green-800' : 'text-gray-500'
                  )}
                >
                  {DAY_LABELS[day]}
                </span>
              </label>

              {/* Time inputs */}
              {slot.is_open ? (
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={slot.open_time}
                    onChange={(e) => updateTimeSlot(day, { open_time: e.target.value })}
                    className="rounded border border-gray-300 px-2 py-1 text-sm focus:border-primary-2.0 focus:outline-none focus:ring-1 focus:ring-primary-2.0"
                  />
                  <span className="text-gray-500">-</span>
                  <input
                    type="time"
                    value={slot.close_time}
                    onChange={(e) => updateTimeSlot(day, { close_time: e.target.value })}
                    className="rounded border border-gray-300 px-2 py-1 text-sm focus:border-primary-2.0 focus:outline-none focus:ring-1 focus:ring-primary-2.0"
                  />
                </div>
              ) : (
                <span className="text-sm text-gray-500">Cerrado</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="border-amber-200 bg-amber-50 p-3 mt-4 border rounded-lg">
        <p className="text-amber-800 text-xs">
          <Icon name={OrbitIcons.AlertCircle} size={12} className="inline mr-1" />
          Las llamadas fuera de este horario se redirigiran al mensaje de fuera de horario.
        </p>
      </div>
    </>
  );
};

/* ───────────────────────── Audios ──────────────────────────── */

interface AudioMoment {
  id: string;
  label: string;
  section: 'in_hours' | 'out_of_hours';
  playbackTypes: PlaybackType[];
  plays: string;
}

const AUDIO_MOMENT_SECTIONS: Record<AudioMoment['section'], string> = {
  in_hours: 'En horario',
  out_of_hours: 'Fuera de horario',
};

// IVR audios are picked and uploaded in the IVR step, and the out-of-hours
// message keeps its default audio, so neither is listed here.
const AUDIO_MOMENTS: AudioMoment[] = [
  {
    id: 'welcome',
    label: 'Bienvenida',
    section: 'in_hours',
    playbackTypes: ['greeting'],
    plays:
      'Es lo primero que escucha el paciente al llamar, antes de conectar con un agente. Si la cuenta tiene menú IVR, suena después de elegir una opción.',
  },
  {
    id: 'waiting',
    label: 'Mientras espera',
    section: 'in_hours',
    playbackTypes: ['hold_music', 'announce'],
    plays:
      'Mientras el paciente espera en la cola: música de fondo y anuncios periódicos de su posición.',
  },
];

const AUDIO_ACCEPT = 'audio/*,.wav,.mp3,.ogg,.m4a,.aac';

const isUsingDefaultAudio = (audio: MockAudio): boolean =>
  Boolean(audio.is_default && audio.default_url && !audio.file);

const getAudioSrc = (audio: MockAudio): string | null => {
  if (audio.preview_url) return audio.preview_url;
  if (audio.is_default && audio.default_url) return audio.default_url;
  return null;
};

const AudioMomentList: React.FC<{
  audios: MockAudio[];
  usages: Record<string, string[]>;
  playingId: string | null;
  registerFileInput: (id: string, el: HTMLInputElement | null) => void;
  openFilePicker: (id: string) => void;
  onTogglePlay: (audio: MockAudio) => void;
  onFileSelect: (id: string, fileList: FileList | null) => void;
  onClearFile: (id: string) => void;
  onRemove: (id: string) => void;
  onAdd: (type: PlaybackType) => void;
}> = ({
  audios,
  usages,
  playingId,
  registerFileInput,
  openFilePicker,
  onTogglePlay,
  onFileSelect,
  onClearFile,
  onRemove,
  onAdd,
}) => (
  <div>
    <h4 className="flex items-center gap-2 text-sm font-bold text-black-1.0">
      <Icon name={OrbitIcons.PhoneIncoming} size={16} className="text-primary-2.0" />
      Recorrido de la llamada
    </h4>
    <p className="mt-1 mb-4 text-xs text-gray-500">Los momentos de la llamada, en orden.</p>

    <ol>
      {AUDIO_MOMENTS.map((moment, index) => {
        const momentAudios = audios.filter((a) => moment.playbackTypes.includes(a.playback_type));
        const isLast = index === AUDIO_MOMENTS.length - 1;
        const startsSection = index === 0 || AUDIO_MOMENTS[index - 1].section !== moment.section;

        return (
          <li key={moment.id}>
            {startsSection && (
              <p className="mb-2 text-[11px] font-semibold tracking-wide text-gray-500 uppercase">
                {AUDIO_MOMENT_SECTIONS[moment.section]}
              </p>
            )}
            <div className="flex gap-3">
              <div className="flex flex-col items-center">
                <span className="bg-primary-2.0 flex items-center justify-center flex-shrink-0 w-6 h-6 text-xs font-bold text-white rounded-full">
                  {index + 1}
                </span>
                {!isLast && <span className="flex-1 w-px my-1 bg-gray-300" />}
              </div>

              <div className={classNames('flex-1 min-w-0', isLast ? 'pb-0' : 'pb-5')}>
                <p className="text-sm font-semibold text-black-1.0">{moment.label}</p>
                <p className="mt-0.5 text-xs text-gray-500">{moment.plays}</p>

                <div className="mt-2 space-y-1.5">
                  {momentAudios.length === 0 && (
                    <p className="text-xs italic text-gray-400">Sin audios en este momento</p>
                  )}
                  {momentAudios.map((audio) => {
                    const playing = audio.id === playingId;
                    const src = getAudioSrc(audio);
                    const audioUsages = usages[audio.id] ?? [];
                    const name = audio.name || 'Sin nombre';

                    return (
                      <div
                        key={audio.id}
                        className="border-white-2.0 bg-white-0.0 flex flex-wrap items-center gap-2 px-2 py-1.5 border rounded-lg"
                      >
                        <button
                          type="button"
                          onClick={() => onTogglePlay(audio)}
                          disabled={!src}
                          aria-label={`${playing ? 'Pausar' : 'Reproducir'} ${audio.name}`}
                          className="hover:bg-gray-200 w-7 h-7 disabled:opacity-40 flex items-center justify-center flex-shrink-0 text-gray-700 bg-gray-100 rounded-full"
                        >
                          <Icon name={playing ? OrbitIcons.Pause : OrbitIcons.Play} size={12} />
                        </button>

                        <div className="flex items-center flex-1 min-w-[140px] gap-2">
                          <span className="text-black-1.0 text-sm truncate">{name}</span>
                          {isUsingDefaultAudio(audio) && (
                            <span className="text-[10px] font-medium text-primary-2.0 bg-primary-2.0/10 px-2 py-0.5 rounded-full flex-shrink-0">
                              Ejemplo
                            </span>
                          )}
                          {audioUsages.length > 0 && (
                            <span
                              title={audioUsages.join(', ')}
                              className="text-amber-800 bg-amber-100 text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0"
                            >
                              En uso
                            </span>
                          )}
                          {audio.file ? (
                            <span
                              className="max-w-[160px] text-xs text-gray-400 truncate"
                              title={audio.file_name}
                            >
                              {audio.file_name}
                            </span>
                          ) : (
                            !isUsingDefaultAudio(audio) && (
                              <span className="text-xs text-gray-400">Sin archivo</span>
                            )
                          )}
                        </div>

                        <div className="flex items-center gap-2 ml-auto">
                          <input
                            ref={(el) => registerFileInput(audio.id, el)}
                            type="file"
                            accept={AUDIO_ACCEPT}
                            aria-label={`Archivo de audio ${name}`}
                            className="hidden"
                            onChange={(e) => onFileSelect(audio.id, e.target.files)}
                          />
                          <button
                            type="button"
                            onClick={() => openFilePicker(audio.id)}
                            className="hover:bg-gray-200 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors bg-gray-100 rounded-lg"
                          >
                            <Icon name={OrbitIcons.Download} size={12} />
                            {audio.file ? 'Cambiar' : 'Subir propio'}
                          </button>
                          {audio.file && (
                            <button
                              type="button"
                              onClick={() => onClearFile(audio.id)}
                              className="hover:text-red-700 text-xs text-red-500"
                            >
                              {audio.default_url ? 'Restaurar ejemplo' : 'Quitar'}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => onRemove(audio.id)}
                            aria-label={`Eliminar ${name}`}
                            className="hover:text-red-700 text-red-500"
                          >
                            <Icon name={OrbitIcons.Trash} size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => onAdd(moment.playbackTypes[0])}
                  className="hover:underline inline-flex items-center gap-1 mt-2 text-xs font-medium text-primary-2.0"
                >
                  + Agregar audio
                </button>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  </div>
);

export const AudiosStep: React.FC<{
  audios: MockAudio[];
  onChange: (audios: MockAudio[]) => void;
  usages?: Record<string, string[]>;
}> = ({ audios, onChange, usages = {} }) => {
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const previewRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);

  const stopPreview = (): void => {
    previewRef.current?.pause();
    previewRef.current = null;
    setPlayingId(null);
  };

  useEffect(
    () => () => {
      previewRef.current?.pause();
      previewRef.current = null;
    },
    []
  );

  const togglePreview = (audio: MockAudio): void => {
    const wasPlaying = playingId === audio.id;
    stopPreview();
    const src = getAudioSrc(audio);
    if (wasPlaying || !src) return;

    const player = new Audio(src);
    player.onended = () => {
      if (previewRef.current === player) stopPreview();
    };
    previewRef.current = player;
    setPlayingId(audio.id);
    void player.play().catch(() => {
      if (previewRef.current === player) stopPreview();
    });
  };

  const revokePreview = (url?: string | null): void => {
    if (url) URL.revokeObjectURL(url);
  };

  const updateAudio = (id: string, patch: Partial<MockAudio>): void => {
    onChange(audios.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  };

  const handleFileSelect = (id: string, fileList: FileList | null): void => {
    const file = fileList?.[0];
    if (!file) return;

    const current = audios.find((a) => a.id === id);
    revokePreview(current?.preview_url);
    if (playingId === id) stopPreview();

    const preview_url = URL.createObjectURL(file);
    const baseName = file.name.replace(/\.[^.]+$/, '');
    // Remove "(ejemplo)" suffix when replacing default audio
    const cleanedName = current?.name?.replace(/\s*\(ejemplo\)$/i, '').trim();
    const keepName = cleanedName && cleanedName !== 'Nuevo audio' ? cleanedName : baseName;

    updateAudio(id, {
      file,
      file_name: file.name,
      preview_url,
      name: keepName,
      is_default: false, // No longer using default
    });
  };

  const clearFile = (id: string): void => {
    const current = audios.find((a) => a.id === id);
    revokePreview(current?.preview_url);
    if (playingId === id) stopPreview();
    // Restore default state if it had a default_url
    if (current?.default_url) {
      const defaultName = current.name.includes('(ejemplo)')
        ? current.name
        : `${current.name} (ejemplo)`;
      updateAudio(id, {
        file: null,
        file_name: current.default_url.split('/').pop() || '',
        preview_url: null,
        name: defaultName,
        is_default: true,
      });
    } else {
      updateAudio(id, { file: null, file_name: '', preview_url: null });
    }
    const input = fileInputRefs.current[id];
    if (input) input.value = '';
  };

  const addAudio = (playbackType: PlaybackType): void => {
    const id = `a${Date.now()}`;
    onChange([
      ...audios,
      {
        id,
        name: 'Nuevo audio',
        playback_type: playbackType,
        file_name: '',
        file: null,
        preview_url: null,
        is_default: false,
      },
    ]);
  };

  const removeAudio = (id: string): void => {
    const current = audios.find((a) => a.id === id);
    revokePreview(current?.preview_url);
    if (playingId === id) stopPreview();

    onChange(audios.filter((a) => a.id !== id));
  };

  return (
    <div className="p-5 overflow-y-auto border border-gray-200 rounded-2xl bg-white-0.0">
      <AudioMomentList
        audios={audios}
        usages={usages}
        playingId={playingId}
        registerFileInput={(id, el) => {
          fileInputRefs.current[id] = el;
        }}
        openFilePicker={(id) => fileInputRefs.current[id]?.click()}
        onTogglePlay={togglePreview}
        onFileSelect={handleFileSelect}
        onClearFile={clearFile}
        onRemove={removeAudio}
        onAdd={addAudio}
      />
    </div>
  );
};

/* ───────────────────────── Resolution Strategies ─────────────────── */

export const ResolutionStrategyStep: React.FC<{
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
}> = ({ state, onChange }) => {
  const { resolution_strategies } = state;

  const updateStrategies = (strategies: MockResolutionStrategy[]): void => {
    onChange({ resolution_strategies: strategies });
  };

  const addStrategy = (): void => {
    const newStrategy: MockResolutionStrategy = {
      id: `strategy_${Date.now()}`,
      name: `Estrategia ${resolution_strategies.length + 1}`,
      resolution_time: 2,
      tags: [
        { id: `t_${Date.now()}_1`, name: 'Resuelto' },
        { id: `t_${Date.now()}_2`, name: 'Callback' },
      ],
    };
    updateStrategies([...resolution_strategies, newStrategy]);
  };

  const removeStrategy = (strategyId: string): void => {
    updateStrategies(resolution_strategies.filter((s) => s.id !== strategyId));
  };

  const updateStrategy = (strategyId: string, patch: Partial<MockResolutionStrategy>): void => {
    updateStrategies(
      resolution_strategies.map((s) => (s.id === strategyId ? { ...s, ...patch } : s))
    );
  };

  const addTag = (strategyId: string): void => {
    const strategy = resolution_strategies.find((s) => s.id === strategyId);
    if (!strategy) return;

    const newTag: MockResolutionTag = {
      id: `t_${Date.now()}`,
      name: '',
    };
    updateStrategy(strategyId, { tags: [...strategy.tags, newTag] });
  };

  const removeTag = (strategyId: string, tagId: string): void => {
    const strategy = resolution_strategies.find((s) => s.id === strategyId);
    if (!strategy) return;

    updateStrategy(strategyId, { tags: strategy.tags.filter((t) => t.id !== tagId) });
  };

  const updateTag = (strategyId: string, tagId: string, name: string): void => {
    const strategy = resolution_strategies.find((s) => s.id === strategyId);
    if (!strategy) return;

    updateStrategy(strategyId, {
      tags: strategy.tags.map((t) => (t.id === tagId ? { ...t, name } : t)),
    });
  };

  return (
    <>
      <SectionHeader
        icon={OrbitIcons.CheckCircle2}
        title="Clasificación de llamadas"
        subtitle="¿Cómo quieres categorizar las llamadas atendidas?"
      />

      {/* Explanation box */}
      <div className="bg-blue-50 p-3 mb-4 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          Al colgar, el agente selecciona una etiqueta (ej: &quot;Cita agendada&quot;,
          &quot;Consulta de facturación&quot;) para generar reportes.
        </p>
      </div>

      <div className="space-y-4">
        {resolution_strategies.map((strategy) => (
          <div
            key={strategy.id}
            className="rounded-lg border border-white-2.0 bg-white-1.0 p-4 space-y-3"
          >
            {/* Strategy header */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className={labelClass}>Nombre de la estrategia</label>
                <input
                  className={inputClass}
                  value={strategy.name}
                  onChange={(e) => updateStrategy(strategy.id, { name: e.target.value })}
                  placeholder="Ej: Clasificación de llamadas"
                />
              </div>
              <div>
                <label className={labelClass}>Tiempo para clasificar</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={10}
                    className={classNames(inputClass, 'w-20 text-center')}
                    value={strategy.resolution_time}
                    onChange={(e) =>
                      updateStrategy(strategy.id, {
                        resolution_time: Math.max(1, parseInt(e.target.value, 10) || 1),
                      })
                    }
                  />
                  <span className="text-sm text-gray-2.0">minutos</span>
                </div>
              </div>
            </div>

            {/* Tags section */}
            <div>
              <label className={labelClass}>Etiquetas ({strategy.tags.length})</label>
              <p className="text-xs text-gray-2.0 mb-2">
                Los agentes seleccionarán una de estas etiquetas al terminar la llamada
              </p>

              <div className="flex flex-wrap gap-2 mb-2">
                {strategy.tags.map((tag) => (
                  <div
                    key={tag.id}
                    className="flex items-center gap-1 rounded-full border border-white-2.0 bg-white-0.0 pl-3 pr-1 py-1"
                  >
                    <input
                      type="text"
                      className="focus:outline-none w-32 text-sm bg-transparent border-none"
                      value={tag.name}
                      onChange={(e) => updateTag(strategy.id, tag.id, e.target.value)}
                      placeholder="Nombre de etiqueta"
                    />
                    <button
                      type="button"
                      onClick={() => removeTag(strategy.id, tag.id)}
                      className="p-1 rounded-full hover:bg-white-2.0 text-gray-2.0 hover:text-red-600"
                      title="Quitar etiqueta"
                    >
                      <Icon name={OrbitIcons.X} size={12} />
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => addTag(strategy.id)}
                  className="flex items-center gap-1 rounded-full border border-dashed border-white-2.0 px-3 py-1 text-sm text-gray-2.0 hover:border-primary-2.0 hover:text-primary-2.0"
                >
                  <Icon name={OrbitIcons.PlusCircle} size={12} />
                  Agregar
                </button>
              </div>
            </div>

            {/* Delete strategy button */}
            {resolution_strategies.length > 1 && (
              <div className="flex justify-end pt-2 border-t border-white-2.0">
                <button
                  type="button"
                  onClick={() => removeStrategy(strategy.id)}
                  className="btn btn-ghost text-xs text-red-600"
                >
                  Eliminar estrategia
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Add strategy button */}
        <button
          type="button"
          onClick={addStrategy}
          className="btn btn-ghost inline-flex items-center gap-2 text-sm"
        >
          <Icon name={OrbitIcons.PlusCircle} size={14} />
          Agregar otra estrategia
        </button>
      </div>
    </>
  );
};

/* ───────────────────────── Modules ─────────────────────────── */

const toSnakeCase = (str: string): string =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .trim()
    .replace(/\s+/g, '_'); // Replace spaces with underscores

const RING_STRATEGY_HELP = {
  round_robin:
    'Distribuye las llamadas de forma equitativa entre todos los agentes disponibles, rotando en cada llamada.',
  fixed_order:
    'Siempre intenta primero con el agente 1, luego el 2, etc. Útil cuando hay un agente principal.',
};

export const ModulesStep: React.FC<{
  state: WizardState;
  onChange: (modules: MockModule[]) => void;
}> = ({ state, onChange }) => {
  const audios = state.audios;
  const modules = state.modules;
  const [ringStrategies, setRingStrategies] = useState<RingStrategy[]>([]);
  const [strategiesLoading, setStrategiesLoading] = useState(true);

  useEffect(() => {
    const loadStrategies = async (): Promise<void> => {
      try {
        const strategies = await fetchRingStrategies();
        setRingStrategies(strategies);
      } catch {
        // Fallback to hardcoded if API fails
        setRingStrategies([
          { id: 1, name: 'round_robin' },
          { id: 2, name: 'fixed_order' },
        ]);
      } finally {
        setStrategiesLoading(false);
      }
    };
    void loadStrategies();
  }, []);

  // Create a default module when the step loads and no modules exist
  useEffect(() => {
    if (strategiesLoading || modules.length > 0) return;

    const id = `m${Date.now()}`;
    const suggestedName = state.account_name ? toSnakeCase(state.account_name) : 'nuevo_modulo';
    const defaultStrategy = ringStrategies[0]?.name || 'round_robin';
    onChange([
      {
        id,
        name: suggestedName,
        phone_number: state.phone_number,
        in_office_greeting_id: audios.find((a) => a.playback_type === 'greeting')?.id || '',
        in_office_hold_id: audios.find((a) => a.playback_type === 'hold_music')?.id || '',
        in_office_announce_id: audios.find((a) => a.playback_type === 'announce')?.id || '',
        out_of_office_message_id: audios.find((a) => a.playback_type === 'message')?.id || '',
        ring_strategy: defaultStrategy,
        max_wait_time: 300,
      },
    ]);
  }, [
    strategiesLoading,
    modules.length,
    state.account_name,
    state.phone_number,
    audios,
    ringStrategies,
    onChange,
  ]);

  const updateModule = (id: string, patch: Partial<MockModule>): void => {
    onChange(modules.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };

  const audioOptions = (type?: PlaybackType): MockAudio[] =>
    audios.filter((a) => !type || a.playback_type === type || a.playback_type === 'ivr_prompt');

  return (
    <>
      <SectionHeader
        icon={OrbitIcons.Workflow}
        title="Línea principal"
        subtitle="Configura cómo se comporta la línea telefónica"
      />

      <div className="space-y-4">
        {modules.map((mod) => (
          <div
            key={mod.id}
            className="rounded-lg border border-white-2.0 bg-white-1.0 p-4 space-y-3"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-black-1.0">{mod.name}</h4>
              </div>
              <span className="font-mono text-[11px] text-gray-2.0">{moduleQueueKey(mod.id)}</span>
            </div>

            <div className="gap-x-3 gap-y-2 grid grid-cols-2">
              <div>
                <label className={labelClass}>Nombre</label>
                <input
                  className={inputClass}
                  value={mod.name}
                  onChange={(e) => updateModule(mod.id, { name: e.target.value })}
                />
              </div>
              <div>
                <label className={labelClass}>Phone number</label>
                <input
                  className={inputClass}
                  value={mod.phone_number}
                  onChange={(e) => updateModule(mod.id, { phone_number: e.target.value })}
                />
              </div>
              <div>
                <label className={classNames(labelClass, 'flex items-center gap-1')}>
                  Ring strategy
                  <span
                    title={`round_robin: ${RING_STRATEGY_HELP.round_robin}\n\nfixed_order: ${RING_STRATEGY_HELP.fixed_order}`}
                    className="cursor-help"
                  >
                    <Icon name={OrbitIcons.HelpCircle} size={14} className="text-gray-2.0" />
                  </span>
                </label>
                <Select
                  options={ringStrategies}
                  fieldId="name"
                  fieldLabel="name"
                  value={mod.ring_strategy}
                  isDisabled={strategiesLoading}
                  isLoading={strategiesLoading}
                  onChange={(opt: unknown) => {
                    const selected = opt as { name: string } | null;
                    if (selected) {
                      updateModule(mod.id, {
                        ring_strategy: selected.name as MockModule['ring_strategy'],
                      });
                    }
                  }}
                  menuPortalTarget={document.body}
                />
              </div>
              <div>
                <label className={labelClass}>Max wait (seg)</label>
                <input
                  type="number"
                  className={inputClass}
                  value={mod.max_wait_time}
                  onChange={(e) =>
                    updateModule(mod.id, { max_wait_time: Number(e.target.value) || 0 })
                  }
                />
              </div>
            </div>

            <div className="space-y-2 rounded-md border border-white-2.0 bg-white-0.0 p-3">
              <p className="text-xs font-semibold text-black-1.0">
                📞 Dentro del horario (las llamadas van a los agentes)
              </p>
              <div className="grid grid-cols-3 gap-2">
                {(
                  [
                    ['in_office_greeting_id', 'Saludo inicial', 'greeting'],
                    ['in_office_hold_id', 'Música de espera', 'hold_music'],
                    ['in_office_announce_id', 'Anuncio de posición', 'announce'],
                  ] as const
                ).map(([field, label, type]) => (
                  <div key={field}>
                    <label className={labelClass}>{label}</label>
                    <Select
                      options={audioOptions(type)}
                      fieldId="id"
                      fieldLabel="name"
                      value={mod[field]}
                      onChange={(opt: unknown) => {
                        const selected = opt as { id: string } | null;
                        if (selected) updateModule(mod.id, { [field]: selected.id });
                      }}
                      menuPortalTarget={document.body}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2 rounded-md border border-white-2.0 bg-white-0.0 p-3">
              <p className="text-xs font-semibold text-black-1.0">
                🌙 Fuera del horario (mensaje automático)
              </p>
              <div>
                <label className={labelClass}>Mensaje de buzón</label>
                <Select
                  options={audioOptions('message')}
                  fieldId="id"
                  fieldLabel="name"
                  value={mod.out_of_office_message_id}
                  onChange={(opt: unknown) => {
                    const selected = opt as { id: string } | null;
                    if (selected) updateModule(mod.id, { out_of_office_message_id: selected.id });
                  }}
                  menuPortalTarget={document.body}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

/* ───────────────────────── IVR ─────────────────────────────── */

/** Map greeting_type number to label */
const GREETING_TYPE_LABELS: Record<number, string> = {
  0: 'Principal',
  1: 'Fuera de horario',
  2: 'Vacaciones',
};

/** Check if an audio entry has at least one valid URL */
const hasValidAudioUrl = (audio: import('../../api/cosmos').CosmosIvrAudioMessage): boolean => {
  return !!(
    audio.selection_audio_link ||
    audio.ivr_selection_audio_file ||
    audio.audio_link ||
    audio.ivr_audio_file
  );
};

/** Collect all audios from an option and its nested children (only those with valid URLs) */
const collectOptionAudios = (
  opt: import('../../api/cosmos').CosmosIvrOption
): Array<{ lang: string; audio: import('../../api/cosmos').CosmosIvrAudioMessage }> => {
  const audios: Array<{ lang: string; audio: import('../../api/cosmos').CosmosIvrAudioMessage }> =
    [];

  // Collect from root option (only if has valid URLs)
  if (opt.audio_message) {
    Object.entries(opt.audio_message).forEach(([lang, audio]) => {
      if (hasValidAudioUrl(audio)) {
        audios.push({ lang, audio });
      }
    });
  }

  // Collect from nested options recursively
  if (opt.options && opt.options.length > 0) {
    opt.options.forEach((childOpt) => {
      const childAudios = collectOptionAudios(childOpt);
      audios.push(...childAudios);
    });
  }

  return audios;
};

/** Collapsible IVR option row */
const CollapsibleIvrOption: React.FC<{
  opt: import('../../api/cosmos').CosmosIvrOption;
}> = ({ opt }) => {
  const [expanded, setExpanded] = useState(false);
  const allAudios = collectOptionAudios(opt);
  const hasAudios = allAudios.length > 0;

  return (
    <div className="p-2 text-xs">
      <button
        type="button"
        onClick={() => hasAudios && setExpanded(!expanded)}
        className={classNames(
          'flex w-full items-center gap-3 text-left',
          hasAudios && 'cursor-pointer hover:bg-white-1.0 -mx-2 px-2 py-1 rounded transition-colors'
        )}
        disabled={!hasAudios}
      >
        <span className="flex-shrink-0 flex h-6 w-6 items-center justify-center rounded-full bg-primary-2.0 font-bold text-white-0.0">
          {opt.key}
        </span>
        <span className="font-medium text-black-1.0">{opt.display_name}</span>
        {hasAudios && (
          <>
            <span className="text-gray-2.0">
              ({allAudios.length} audio{allAudios.length > 1 ? 's' : ''})
            </span>
            <Icon
              name={OrbitIcons.ChevronRight}
              size={14}
              className={classNames(
                'ml-auto text-gray-2.0 transition-transform',
                expanded && 'rotate-90'
              )}
            />
          </>
        )}
      </button>
      {expanded && hasAudios && (
        <div className="ml-9 mt-2 space-y-2">
          {allAudios.map(({ lang, audio }, idx) => {
            const selectionUrl = audio.selection_audio_link || audio.ivr_selection_audio_file;
            const responseUrl = audio.audio_link || audio.ivr_audio_file;
            if (!selectionUrl && !responseUrl) return null;
            return (
              <div
                key={`${lang}-${idx}`}
                className="rounded border border-white-2.0 bg-white-1.0 p-2"
              >
                <p className="mb-1 text-[10px] font-medium text-gray-3.0">
                  {lang.startsWith('es') ? 'Español' : 'English'}
                </p>
                {selectionUrl && (
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-16 text-[10px] text-gray-2.0">Selección:</span>
                    <audio controls className="flex-1 h-6">
                      <source src={selectionUrl} />
                    </audio>
                  </div>
                )}
                {responseUrl && (
                  <div className="flex items-center gap-2">
                    <span className="w-16 text-[10px] text-gray-2.0">Respuesta:</span>
                    <audio controls className="flex-1 h-6">
                      <source src={responseUrl} />
                    </audio>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Preview component for Cosmos IVR configuration */
const CosmosIvrPreview: React.FC<{ state: WizardState }> = ({ state }) => {
  const { cosmos_ivr } = state;
  if (!cosmos_ivr) return null;

  const rootOptions = cosmos_ivr.options.filter((o) => o.is_root);

  return (
    <div className="space-y-3">
      <div className="bg-green-50 p-3 border border-green-200 rounded-lg">
        <p className="mb-2 text-xs font-medium text-green-800">
          Configuracion IVR de Cosmos (se importaran los audios)
        </p>
        <p className="text-xs text-green-700">
          Idioma: {cosmos_ivr.setting?.language === 'en' ? 'English' : 'Espanol'}
        </p>
      </div>

      {cosmos_ivr.greetings.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-semibold text-gray-3.0">Saludos</p>
          <div className="grid grid-cols-2 gap-2">
            {cosmos_ivr.greetings.map((g) => (
              <div key={g.id} className="rounded border border-white-2.0 bg-white-1.0 p-2 text-xs">
                <span className="font-medium text-black-1.0">
                  {GREETING_TYPE_LABELS[g.greeting_type] || `Tipo ${g.greeting_type}`}
                </span>
                {g.active && <span className="ml-2 text-green-600">(activo)</span>}
                {g.ivr_greeting_audio_file && (
                  <audio controls className="w-full h-6 mt-1">
                    <source src={g.ivr_greeting_audio_file} />
                  </audio>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {rootOptions.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-semibold text-gray-3.0">Opciones del menu</p>
          <div className="divide-y divide-white-2.0 rounded-lg border border-white-2.0">
            {rootOptions.map((opt) => (
              <CollapsibleIvrOption key={opt.key} opt={opt} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const IVR_ACTION_LABELS: Record<IvrActionType, string> = {
  module: 'Ir al módulo',
  replay: 'Repetir este menú',
  back: 'Volver al menú anterior',
  submenu: 'Submenú',
};

const ivrInputClass = inputClass.replace('w-full ', '');

const IVR_ICON_BUTTON_CLASS =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white-2.0 bg-white-0.0 text-gray-3.0 transition-colors hover:bg-white-1.0';

const IvrAudioPicker: React.FC<{
  value: string;
  audios: MockAudio[];
  ariaLabel?: string;
  /** Shows the file name next to the audio name in the options */
  showFileName?: boolean;
  onChange: (audioId: string) => void;
  onUpload: (file: File) => void;
}> = ({ value, audios, ariaLabel = 'Audio', showFileName = false, onChange, onUpload }) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const options = showFileName
    ? audios.map((a) => ({ ...a, displayLabel: `${a.name} (${a.file_name})` }))
    : audios.map((a) => ({ ...a, displayLabel: a.name }));

  return (
    <div className="flex items-center flex-1 min-w-0 gap-1">
      <div className="flex-1 min-w-0" aria-label={ariaLabel}>
        <Select
          options={options}
          fieldId="id"
          fieldLabel="displayLabel"
          value={value}
          onChange={(a: unknown) => {
            const selected = a as { id: string } | null;
            if (selected) onChange(selected.id);
          }}
          menuPortalTarget={document.body}
        />
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={AUDIO_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
          if (inputRef.current) inputRef.current.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className={IVR_ICON_BUTTON_CLASS}
        title="Subir audio"
        aria-label="Subir audio"
      >
        <Icon name={OrbitIcons.Download} size={14} />
      </button>
      <AudioPreviewButton audioId={value} audios={audios} />
    </div>
  );
};

interface IvrOptionRowProps {
  option: MockIvrOption;
  /** 1 for root options */
  depth: number;
  /** Digits leading to this option, e.g. "1 › 2" */
  path: string;
  audios: MockAudio[];
  errors: IvrValidationErrors;
  onUpdate: (id: string, patch: Partial<MockIvrOption>) => void;
  onRemove: (id: string) => void;
  onDuplicate: (id: string) => void;
  /** False when the option's menu has no free digit left for a copy */
  canDuplicate: boolean;
  onAddChild: (parentId: string, asSubmenu?: boolean) => void;
  onUploadAudio: (file: File, namePrefix: string) => string;
}

const IvrOptionsHeader: React.FC = () => (
  <div className="flex items-center gap-2 px-2 text-xs font-medium text-gray-3.0">
    <span className="shrink-0 w-12 text-center">Díg.</span>
    <span className="flex-1">Etiqueta</span>
    <span className="shrink-0 w-48">Acción</span>
    <span className="shrink-0 w-[4.5rem]" />
  </div>
);

const IvrOptionRow: React.FC<IvrOptionRowProps> = ({
  option,
  depth,
  path,
  audios,
  errors,
  onUpdate,
  onRemove,
  onDuplicate,
  canDuplicate,
  onAddChild,
  onUploadAudio,
}) => {
  const [expanded, setExpanded] = useState(true);
  const actionType: IvrActionType = option.action_type || 'module';
  const optionErrors = errors.byOption[option.id] ?? [];
  const isSpecial = actionType === 'replay' || actionType === 'back';
  const children = option.children ?? [];
  const canDuplicateChild = nextFreeIvrDigit(children) !== '';

  const actionOptions = [
    { value: 'module', label: IVR_ACTION_LABELS.module },
    { value: 'replay', label: IVR_ACTION_LABELS.replay },
    ...(depth > 1 ? [{ value: 'back', label: IVR_ACTION_LABELS.back }] : []),
    {
      value: 'submenu',
      label: IVR_ACTION_LABELS.submenu,
      isDisabled: depth >= MAX_IVR_DEPTH,
    },
  ];

  const handleActionChange = (next: IvrActionType): void => {
    if (next === 'submenu') {
      onUpdate(option.id, { action_type: next, children });
      setExpanded(true);
    } else {
      onUpdate(option.id, { action_type: next, children: undefined, menu_audio_id: undefined });
    }
  };

  return (
    <div className="space-y-2">
      <div
        className={classNames(
          'space-y-2 rounded-lg border p-2',
          optionErrors.length > 0
            ? 'border-red-300 bg-red-50'
            : isSpecial
            ? 'border-blue-200 bg-blue-50'
            : 'border-white-2.0 bg-white-1.0'
        )}
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="shrink-0 flex items-center gap-1">
            <input
              aria-label={`Dígito ${path}`}
              className={classNames(ivrInputClass, 'w-12 px-1 text-center')}
              value={option.digit}
              maxLength={1}
              onChange={(e) => onUpdate(option.id, { digit: e.target.value })}
            />
          </div>
          <input
            aria-label={`Etiqueta ${path}`}
            className={classNames(ivrInputClass, 'min-w-[8rem] flex-1')}
            value={option.label}
            onChange={(e) => onUpdate(option.id, { label: e.target.value })}
          />
          <div className="shrink-0 w-48">
            <Select
              aria-label={`Acción ${path}`}
              options={actionOptions}
              value={actionType}
              isSearchable={false}
              onChange={(opt: unknown) => {
                const selected = opt as { value: IvrActionType } | null;
                if (selected) handleActionChange(selected.value);
              }}
              menuPortalTarget={document.body}
            />
          </div>
          <button
            type="button"
            onClick={() => onDuplicate(option.id)}
            disabled={!canDuplicate}
            className="shrink-0 hover:bg-white-2.0 inline-flex items-center justify-center w-8 h-8 text-gray-3.0 transition-colors rounded-md disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            aria-label={`Duplicar opción ${path}`}
            title={canDuplicate ? 'Duplicar opción' : 'No quedan dígitos libres en este menú'}
          >
            <Icon name={OrbitIcons.Copy} size={14} />
          </button>
          <button
            type="button"
            onClick={() => onRemove(option.id)}
            className="shrink-0 hover:bg-red-50 inline-flex items-center justify-center w-8 h-8 text-red-600 transition-colors rounded-md"
            aria-label={`Eliminar opción ${option.digit}`}
            title="Eliminar opción"
          >
            <Icon name={OrbitIcons.Trash} size={14} />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-xs font-medium text-gray-3.0">Audio</span>
          <IvrAudioPicker
            value={option.audio_id}
            audios={audios}
            ariaLabel={`Audio ${path}`}
            onChange={(audioId) => onUpdate(option.id, { audio_id: audioId })}
            onUpload={(file) =>
              onUpdate(option.id, {
                audio_id: onUploadAudio(file, `Opcion ${path}`),
              })
            }
          />
        </div>
        {optionErrors.length > 0 && (
          <div className="space-y-0.5">
            {optionErrors.map((message) => (
              <p key={message} className="flex items-center gap-1 text-xs text-red-600">
                <Icon name={OrbitIcons.AlertCircle} size={12} />
                {message}
              </p>
            ))}
          </div>
        )}
      </div>

      {actionType === 'submenu' && (
        <div className="ml-4 space-y-2 border-l-2 border-primary-2.0/40 pl-3">
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            className="inline-flex items-center gap-1 text-xs font-semibold text-black-1.0"
          >
            <Icon name={expanded ? OrbitIcons.ChevronDown : OrbitIcons.ChevronRight} size={12} />
            {`Submenú de la opción ${path}`}
          </button>
          {expanded && (
            <>
              <div className="flex items-center gap-2 px-2">
                <span className="shrink-0 text-xs font-medium text-gray-3.0">Intro (opcional)</span>
                <IvrAudioPicker
                  value={option.menu_audio_id ?? ''}
                  audios={audios}
                  ariaLabel={`Intro del submenú ${path}`}
                  onChange={(audioId) => onUpdate(option.id, { menu_audio_id: audioId })}
                  onUpload={(file) =>
                    onUpdate(option.id, {
                      menu_audio_id: onUploadAudio(file, `Submenu ${path}`),
                    })
                  }
                />
              </div>
              {children.length > 0 && <IvrOptionsHeader />}
              {children.map((child) => (
                <IvrOptionRow
                  key={child.id}
                  option={child}
                  depth={depth + 1}
                  path={`${path} › ${child.digit || '?'}`}
                  audios={audios}
                  errors={errors}
                  onUpdate={onUpdate}
                  onRemove={onRemove}
                  onDuplicate={onDuplicate}
                  canDuplicate={canDuplicateChild}
                  onAddChild={onAddChild}
                  onUploadAudio={onUploadAudio}
                />
              ))}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => onAddChild(option.id)}
                  aria-label={`Agregar opción en ${path}`}
                  className="btn btn-ghost inline-flex items-center gap-2 text-sm"
                >
                  <Icon name={OrbitIcons.PlusCircle} size={14} />
                  Agregar opción
                </button>
                {depth + 1 < MAX_IVR_DEPTH && (
                  <button
                    type="button"
                    onClick={() => onAddChild(option.id, true)}
                    aria-label={`Agregar submenú en ${path}`}
                    className="btn btn-ghost inline-flex items-center gap-2 text-sm"
                  >
                    <Icon name={OrbitIcons.PlusCircle} size={14} />
                    Agregar submenú
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const IvrStep: React.FC<{
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
}> = ({ state, onChange }) => {
  const { ivr, audios, cosmos_ivr, cosmos_ivr_loading, cosmos_ivr_unavailable, use_cosmos_ivr } =
    state;

  const updateIvr = (patch: Partial<typeof ivr>): void => {
    onChange({ ivr: { ...ivr, ...patch } });
  };

  const updateOption = (id: string, patch: Partial<MockIvrOption>): void => {
    updateIvr({ options: updateIvrOptionInTree(ivr.options, id, patch) });
  };

  const validation = validateIvrTree(ivr.options);
  const hasErrors = validation.root.length > 0 || Object.keys(validation.byOption).length > 0;

  /** Creates a new audio entry from an uploaded file and returns its id */
  const createAudioFromFile = (file: File, namePrefix: string): string => {
    const audioId = `ivr_audio_${Date.now()}`;
    const baseName = file.name.replace(/\.[^.]+$/, '');
    const newAudio: MockAudio = {
      id: audioId,
      name: `${namePrefix} - ${baseName}`,
      playback_type: 'ivr_prompt',
      file_name: file.name,
      file,
      preview_url: URL.createObjectURL(file),
      is_default: false,
    };
    onChange({ audios: [...audios, newAudio] });
    return audioId;
  };

  const buildOption = (
    id: string,
    digit: string,
    extra: Partial<MockIvrOption> = {}
  ): MockIvrOption => ({
    id,
    digit,
    label: `Opcion ${digit}`,
    audio_id: audios[0]?.id || '',
    queue_key: `ivr_option_${digit}`,
    action_type: 'module',
    ...extra,
  });

  const addChildOption = (parentId: string, asSubmenu = false): void => {
    const parent = flattenIvrOptions(ivr.options).find((o) => o.id === parentId);
    const digit = nextFreeIvrDigit(parent?.children ?? []);
    const id = `o${Date.now()}`;
    const newOption = asSubmenu
      ? buildOption(id, digit, { action_type: 'submenu', children: [buildOption(`${id}_1`, '1')] })
      : buildOption(id, digit);
    updateIvr({ options: addIvrChildToTree(ivr.options, parentId, newOption) });
  };

  const nextRootDigit = (): string => nextFreeIvrDigit(ivr.options);

  const addOption = (): void => {
    updateIvr({ options: [...ivr.options, buildOption(`o${Date.now()}`, nextRootDigit())] });
  };

  const addSubmenuOption = (): void => {
    const now = Date.now();
    const child = buildOption(`o${now}_1`, '1');
    updateIvr({
      options: [
        ...ivr.options,
        buildOption(`o${now}`, nextRootDigit(), { action_type: 'submenu', children: [child] }),
      ],
    });
  };

  const removeOption = (id: string): void => {
    updateIvr({ options: removeIvrOptionFromTree(ivr.options, id) });
  };

  const duplicateOption = (id: string): void => {
    const now = Date.now();
    let copied = 0;
    const makeId = (): string => {
      copied += 1;
      return `o${now}_copy${copied}`;
    };
    updateIvr({ options: duplicateIvrOptionInTree(ivr.options, id, makeId) });
  };

  // Show loading state while fetching Cosmos IVR
  if (cosmos_ivr_loading) {
    return (
      <>
        <SectionHeader
          icon={OrbitIcons.PhoneIncoming}
          title="Menú de opciones"
          subtitle="Cargando configuración..."
        />
        <div className="flex items-center gap-2 text-gray-2.0">
          <Icon name={OrbitIcons.RefreshCw} size={16} className="animate-spin" />
          <span className="text-sm">Cargando...</span>
        </div>
      </>
    );
  }

  return (
    <>
      <SectionHeader
        icon={OrbitIcons.PhoneIncoming}
        title="Menú de opciones"
        subtitle='"Para citas presione 1, para facturación presione 2..."'
      />

      <div className="space-y-4">
        {cosmos_ivr_unavailable && (
          <div className="border-amber-200 bg-amber-50 p-4 border rounded-lg">
            <p className="text-amber-800 text-sm">
              No se pudo leer el IVR de Cosmos. Si la cuenta tiene el IVR activo, se mantiene así a
              menos que elijas una de las opciones.
            </p>
          </div>
        )}

        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-3.0">¿Cómo quieres configurarlo?</p>
          <div className="flex gap-4">
            {cosmos_ivr && (
              <label className="flex cursor-pointer items-center gap-2 text-sm text-black-1.0">
                <input
                  type="radio"
                  name="ivr_source"
                  checked={ivr.enabled && use_cosmos_ivr}
                  onChange={() =>
                    onChange({
                      use_cosmos_ivr: true,
                      cosmos_ivr_unavailable: false,
                      ivr: { ...ivr, enabled: true },
                    })
                  }
                  className="rounded-full border-white-2.0"
                />
                Importar existente
              </label>
            )}
            <label className="flex cursor-pointer items-center gap-2 text-sm text-black-1.0">
              <input
                type="radio"
                name="ivr_source"
                checked={ivr.enabled && !use_cosmos_ivr}
                onChange={() =>
                  onChange({
                    use_cosmos_ivr: false,
                    cosmos_ivr_unavailable: false,
                    ivr: { ...ivr, enabled: true },
                  })
                }
                className="rounded-full border-white-2.0"
              />
              Crear nuevo
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-black-1.0">
              <input
                type="radio"
                name="ivr_source"
                checked={!ivr.enabled && !cosmos_ivr_unavailable}
                onChange={() =>
                  onChange({
                    use_cosmos_ivr: false,
                    cosmos_ivr_unavailable: false,
                    ivr: { ...ivr, enabled: false },
                  })
                }
                className="rounded-full border-white-2.0"
              />
              No usar menú
            </label>
          </div>
        </div>

        {/* Show message when IVR is disabled */}
        {!ivr.enabled && cosmos_ivr_unavailable ? null : !ivr.enabled ? (
          <div className="border-amber-200 bg-amber-50 p-4 border rounded-lg">
            <p className="text-amber-800 text-sm">
              Las llamadas entrantes irán directamente al equipo sin pasar por un menú de opciones.
            </p>
          </div>
        ) : cosmos_ivr && use_cosmos_ivr ? (
          <CosmosIvrPreview state={state} />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Idioma</label>
                <Select
                  options={LANGUAGE_OPTIONS}
                  value={ivr.language}
                  onChange={(opt: unknown) => {
                    const selected = opt as { value: string } | null;
                    if (selected) updateIvr({ language: selected.value as 'es' | 'en' });
                  }}
                  menuPortalTarget={document.body}
                />
              </div>
              <div>
                <label className={labelClass}>Audio de bienvenida</label>
                <div className="flex">
                  <IvrAudioPicker
                    value={ivr.welcome_audio_id}
                    audios={audios}
                    ariaLabel="Audio de bienvenida"
                    showFileName
                    onChange={(audioId) => updateIvr({ welcome_audio_id: audioId })}
                    onUpload={(file) =>
                      updateIvr({ welcome_audio_id: createAudioFromFile(file, 'Bienvenida IVR') })
                    }
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-black-1.0">Opciones del menú</p>
                {hasErrors && (
                  <span className="flex items-center gap-1 text-xs text-red-600">
                    <Icon name={OrbitIcons.AlertCircle} size={12} />
                    {validation.root[0] ?? 'Revisa las opciones marcadas'}
                  </span>
                )}
              </div>

              {ivr.options.length > 0 && <IvrOptionsHeader />}

              {ivr.options.map((opt) => (
                <IvrOptionRow
                  key={opt.id}
                  option={opt}
                  depth={1}
                  path={opt.digit || '?'}
                  audios={audios}
                  errors={validation}
                  onUpdate={updateOption}
                  onRemove={removeOption}
                  onDuplicate={duplicateOption}
                  canDuplicate={nextRootDigit() !== ''}
                  onAddChild={addChildOption}
                  onUploadAudio={createAudioFromFile}
                />
              ))}

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={addOption}
                  className="btn btn-ghost inline-flex items-center gap-2 text-sm"
                >
                  <Icon name={OrbitIcons.PlusCircle} size={14} />
                  Agregar opción
                </button>
                <button
                  type="button"
                  onClick={addSubmenuOption}
                  className="btn btn-ghost inline-flex items-center gap-2 text-sm"
                >
                  <Icon name={OrbitIcons.PlusCircle} size={14} />
                  Agregar submenú
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

/* ───────────────────────── Queues / Agents ─────────────────── */

export const QueuesStep: React.FC<{
  state: WizardState;
  onChange: (assignments: WizardState['assignments']) => void;
}> = ({ state, onChange }) => {
  const queues = getAvailableQueues(state);
  const agents = state.users.filter((u) => !u.is_admin);

  const toggle = (userId: string, queueKey: string): void => {
    const current = state.assignments[userId] || [];
    const next = current.includes(queueKey)
      ? current.filter((k) => k !== queueKey)
      : [...current, queueKey];
    onChange({ ...state.assignments, [userId]: next });
  };

  return (
    <>
      <SectionHeader
        icon={OrbitIcons.UserPlus}
        title="Asignar agentes"
        subtitle="¿Quién atiende cada tipo de llamada?"
      />

      {agents.length === 0 ? (
        <div className="bg-blue-50 p-3 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            No hay agentes disponibles. Vuelve al paso Equipo telefónico para agregar alguno.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-white-2.0">
          <table className="w-full text-xs">
            <thead className="bg-white-1.0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-3.0">Agente</th>
                {queues.map((q) => (
                  <th key={q.key} className="px-3 py-2 text-left font-semibold text-gray-3.0">
                    <div className="max-w-[120px] truncate" title={q.key}>
                      {q.label}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => (
                <tr key={agent.id} className="border-t border-white-2.0">
                  <td className="px-3 py-2 font-medium text-black-1.0 whitespace-nowrap">
                    {agent.first_name} {agent.last_name}
                    <div className="font-mono text-[10px] text-gray-2.0">ext {agent.extension}</div>
                  </td>
                  {queues.map((q) => {
                    const checked = (state.assignments[agent.id] || []).includes(q.key);
                    return (
                      <td key={q.key} className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(agent.id, q.key)}
                          className="rounded border-white-2.0"
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-blue-50 p-3 mt-4 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-800">
          Marca las casillas para indicar qué tipos de llamadas puede atender cada agente.
        </p>
      </div>
    </>
  );
};

const PhoneFeatureRow: React.FC<{
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <div className="flex items-center gap-3 border-b border-white-2.0 px-4 py-3 last:border-b-0">
    <div className="flex-1">
      <p className="text-sm font-medium text-black-1.0">{title}</p>
      <p className="text-xs text-gray-2.0">{description}</p>
    </div>
    {children}
  </div>
);

const StatusBadge: React.FC<{ active: boolean; label?: string }> = ({ active, label }) => (
  <span
    className={classNames(
      'rounded px-2 py-0.5 text-[10px] font-semibold',
      active ? 'bg-green-100 text-green-700' : 'bg-white-2.0 text-gray-2.0'
    )}
  >
    {label ?? (active ? 'Activo' : 'Inactivo')}
  </span>
);

const UnavailableReasonsEditor: React.FC<{
  reasons: MockUnavailableReason[];
  onChange: (reasons: MockUnavailableReason[]) => void;
}> = ({ reasons, onChange }) => {
  const errors = getUnavailableReasonErrors(reasons);

  const rename = (id: string, name: string): void => {
    onChange(reasons.map((r) => (r.id === id ? { ...r, name } : r)));
  };

  const move = (index: number, offset: -1 | 1): void => {
    const next = [...reasons];
    [next[index], next[index + offset]] = [next[index + offset], next[index]];
    onChange(next);
  };

  return (
    <div className="mt-4 rounded-lg border border-white-2.0 bg-white-1.0 p-4">
      <p className="text-sm font-medium text-black-1.0">Motivos de No disponible</p>
      <p className="mb-3 text-xs text-gray-2.0">
        Los agentes los verán en este orden. Puedes agregar un emoji al final del nombre como ícono.
        Luego se pueden editar desde el detalle de la cuenta.
      </p>

      <ul className="space-y-2">
        {reasons.map((reason, index) => (
          <li key={reason.id}>
            <div className="flex items-center gap-2">
              <input
                className={classNames(inputClass, errors[reason.id] && inputErrorClass)}
                aria-label={`Motivo ${index + 1}`}
                value={reason.name}
                maxLength={UNAVAILABLE_REASON_MAX_LENGTH}
                onChange={(e) => rename(reason.id, e.target.value)}
                placeholder="Ej: Almuerzo 🍽️"
              />
              <button
                type="button"
                aria-label={`Subir motivo ${index + 1}`}
                disabled={index === 0}
                onClick={() => move(index, -1)}
                className="p-1 text-gray-2.0 hover:text-primary-2.0 disabled:opacity-30"
              >
                <Icon name={OrbitIcons.ChevronDown} size={14} className="rotate-180" />
              </button>
              <button
                type="button"
                aria-label={`Bajar motivo ${index + 1}`}
                disabled={index === reasons.length - 1}
                onClick={() => move(index, 1)}
                className="p-1 text-gray-2.0 hover:text-primary-2.0 disabled:opacity-30"
              >
                <Icon name={OrbitIcons.ChevronDown} size={14} />
              </button>
              <button
                type="button"
                aria-label={`Quitar motivo ${index + 1}`}
                onClick={() => onChange(reasons.filter((r) => r.id !== reason.id))}
                className="p-1 text-gray-2.0 hover:text-red-600"
              >
                <Icon name={OrbitIcons.Trash} size={14} />
              </button>
            </div>
            {errors[reason.id] && <span className={errorClass}>{errors[reason.id]}</span>}
          </li>
        ))}
      </ul>

      {reasons.length === 0 && (
        <p role="alert" className={errorClass}>
          Agrega al menos un motivo o desactiva la función.
        </p>
      )}

      <button
        type="button"
        onClick={() => onChange([...reasons, { id: `ur_${Date.now()}`, name: '' }])}
        className="btn btn-ghost inline-flex items-center gap-2 mt-3 text-sm"
      >
        <Icon name={OrbitIcons.PlusCircle} size={14} />
        Agregar motivo
      </button>
    </div>
  );
};

export const PhoneFeaturesStep: React.FC<{
  state: WizardState;
  hasIvrStep: boolean;
  onChange: (patch: Partial<WizardState>) => void;
}> = ({ state, hasIvrStep, onChange }) => {
  const { phone_features: features } = state;
  const featuresKnown = state.cosmos_phone_features !== null;
  const ivrActive = isIvrFeatureActive(state, hasIvrStep);

  const toggle = (feature: keyof WizardState['phone_features']): void => {
    onChange({ phone_features: { ...features, [feature]: !features[feature] } });
  };

  return (
    <>
      <SectionHeader
        icon={OrbitIcons.Phone}
        title="Funciones del teléfono"
        subtitle="¿Qué funciones del softphone tendrá la cuenta?"
      />

      <div className="rounded-lg border border-white-2.0">
        <PhoneFeatureRow
          title="Softphone"
          description="La cuenta recibe y hace llamadas desde BrainHi"
        >
          <StatusBadge active label="Siempre activo" />
        </PhoneFeatureRow>
        {!featuresKnown && (
          <p role="status" className="text-amber-700 px-4 py-3 text-sm">
            No se pudieron leer las funciones que la cuenta ya tiene en Cosmos. Solo se activará el
            softphone; conferencia, call whisper, motivos de No disponible e IVR quedan como están
            en Cosmos.
          </p>
        )}
        {featuresKnown && (
          <>
            <PhoneFeatureRow
              title="Conferencia"
              description="Permite sumar a un tercero a una llamada en curso"
            >
              <input
                type="checkbox"
                aria-label="Conferencia"
                checked={features.conference}
                onChange={() => toggle('conference')}
                className="rounded border-white-2.0"
              />
            </PhoneFeatureRow>
            <PhoneFeatureRow
              title="Call whisper"
              description="Los account managers pueden escuchar y hablarle al agente durante una llamada"
            >
              <input
                type="checkbox"
                aria-label="Call whisper"
                checked={features.call_whisper}
                onChange={() => toggle('call_whisper')}
                className="rounded border-white-2.0"
              />
            </PhoneFeatureRow>
            <PhoneFeatureRow
              title="Motivos de No disponible"
              description="Los agentes eligen un motivo al ponerse No disponible y los supervisores lo ven"
            >
              <input
                type="checkbox"
                aria-label="Motivos de No disponible"
                checked={features.unavailable_reasons}
                onChange={() => toggle('unavailable_reasons')}
                className="rounded border-white-2.0"
              />
            </PhoneFeatureRow>
            <PhoneFeatureRow
              title="IVR"
              description={
                hasIvrStep
                  ? 'Se define en el paso IVR'
                  : 'Este flujo no configura IVR; se mantiene como está en Cosmos'
              }
            >
              <StatusBadge active={ivrActive} />
            </PhoneFeatureRow>
          </>
        )}
      </div>

      {featuresKnown && features.unavailable_reasons && (
        <UnavailableReasonsEditor
          reasons={state.unavailable_reasons}
          onChange={(unavailable_reasons) => onChange({ unavailable_reasons })}
        />
      )}
    </>
  );
};
